import type { LDContext as ClientLDContext } from 'launchdarkly-js-client-sdk';
import type { FeatureFlagProfile } from '@repairer-frontend/shared-feature-flags';

type LdContextListAttribute = {
    key: string;
    emptyListValue: string;
};

// Note that the values in ListAttributeKeys must be kept in sync with any changes to
// variables in dbo.usp_Company_RetrieveFeatureFlagProfile.sql in PartsTraderUSA.
// For a new attribute to be treated as a list of values in LaunchDarkly, it must
// be in this list.
const ldContextListAttributes: Array<LdContextListAttribute> = [
    { key: 'DrpMembership', emptyListValue: 'No DRPs' },
];

const isListAttribute = (key: string) =>
    ldContextListAttributes.some(
        (ldContextAttribute) => ldContextAttribute.key === key,
    );

const formatListAttribute = (
    key: string,
    value: string,
): string | Array<string> => {
    if (value) {
        const parsedValues = value
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => entry.length > 0);

        if (parsedValues.length > 0) {
            return parsedValues;
        }
    }

    const listAttributesEntry = ldContextListAttributes.find(
        (ldContextAttribute) => ldContextAttribute.key === key,
    );

    return listAttributesEntry?.emptyListValue ?? '';
};

const normalizeAttributeValue = (key: string, value: string) => {
    if (isListAttribute(key)) {
        return formatListAttribute(key, value);
    }

    return value;
};

export const mapToClientLdContext = (
    featureFlagProfile: FeatureFlagProfile,
): ClientLDContext => {
    const context: ClientLDContext = {
        kind: 'user',
        key: featureFlagProfile.PrimaryKey,
        name: featureFlagProfile.Name,
        _meta: {
            privateAttributes: [],
        },
    };

    for (const [key, value] of Object.entries(
        featureFlagProfile.CustomAttributes,
    )) {
        context[key] = normalizeAttributeValue(key, value);
    }

    for (const [key, value] of Object.entries(
        featureFlagProfile.PrivateCustomAttributes,
    )) {
        context[key] = normalizeAttributeValue(key, value);

        if (!isListAttribute(key)) {
            context._meta?.privateAttributes?.push(key);
        }
    }

    return context;
};
